/**
 * Jediné miesto, ktoré hovorí s API. Relácia je v httpOnly cookies,
 * takže `credentials: 'include'` a nič v localStorage.
 *
 * Po 401 sa raz skúsi /api/auth/refresh (rotácia) a požiadavka sa zopakuje;
 * ak ani to neprejde, volajúci dostane ApiError 401 a router presmeruje na login.
 */
export class ApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly retryAfter?: number) {
    super(message);
    this.name = 'ApiError';
  }
}

async function toError(res: Response): Promise<ApiError> {
  let message = res.statusText || `HTTP ${res.status}`;
  try {
    const body = (await res.json()) as { message?: string | string[] };
    if (body?.message) message = Array.isArray(body.message) ? body.message.join(', ') : body.message;
  } catch { /* telo nie je JSON */ }
  const retry = res.headers.get('Retry-After');
  return new ApiError(res.status, message, retry ? Number(retry) : undefined);
}

let refreshing: Promise<boolean> | null = null;

/** Jeden refresh naraz — súbežné 401 čakajú na ten istý výsledok. */
function refreshSession(): Promise<boolean> {
  refreshing ??= fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => { refreshing = null; });
  return refreshing;
}

const NO_REFRESH = new Set(['/auth/login', '/auth/refresh', '/auth/logout']);

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const doFetch = () =>
    fetch(`/api${path}`, {
      ...init,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    });

  let res = await doFetch();
  if (res.status === 401 && !NO_REFRESH.has(path) && (await refreshSession())) {
    res = await doFetch();
  }
  if (!res.ok) throw await toError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
